// Timeline helpers shared by the live game, Game Info and the timeline editor.
// Events: { id, type, pid, t } with type "buyin" (seated), "rebuy", "undo"
// (a rebuy taken back), "out" or "back".

// An event's identity. Live events carry a random id; games saved before ids
// were kept fall back to type, player and time. The session log Cloud
// Function (functions/sessionLog.js) uses the same key.
export function eventKey(ev) {
  return ev.id || `${ev.type}:${ev.pid}:${ev.t}`;
}

// The events that count: sorted, with each undone rebuy dropped together with
// its undo, so history holds only what counted. Every event keeps an id.
export function cleanGameEvents(events) {
  const sorted = [...(events || [])].sort((a, b) => a.t - b.t);
  const kept = [];
  for (const ev of sorted) {
    if (ev.type === "undo") {
      for (let i = kept.length - 1; i >= 0; i--) {
        if (kept[i].pid === ev.pid && kept[i].type === "rebuy") { kept.splice(i, 1); break; }
      }
    } else {
      kept.push({ id: eventKey(ev), type: ev.type, pid: ev.pid, t: ev.t });
    }
  }
  return kept;
}

// Applies a timeline edit to the events as they are now. `original` is the
// list the editor opened with, `draft` the edited one. Events added by
// someone else since the editor opened are kept. Returns the new events and,
// per player, how many rebuys were added (+) or removed (-).
export function applyTimelineEdit(currentEvents, original, draft) {
  const base = cleanGameEvents(currentEvents);
  const originalIds = new Set(original.map((ev) => ev.id));
  const draftById = new Map(draft.map((ev) => [ev.id, ev]));
  const rebuyDelta = {};
  const bump = (pid, n) => { rebuyDelta[pid] = (rebuyDelta[pid] || 0) + n; };
  const events = [];
  for (const ev of base) {
    if (!originalIds.has(ev.id)) { events.push(ev); continue; }
    const edited = draftById.get(ev.id);
    if (!edited) {
      if (ev.type === "rebuy") bump(ev.pid, -1);
      continue;
    }
    events.push({ ...ev, t: edited.t });
  }
  for (const ev of draft) {
    if (originalIds.has(ev.id)) continue;
    events.push({ id: ev.id, type: ev.type, pid: ev.pid, t: ev.t });
    if (ev.type === "rebuy") bump(ev.pid, 1);
  }
  events.sort((a, b) => a.t - b.t);
  return { events, rebuyDelta };
}

// Who is out after these events, for players with any out/back event.
export function outFromEvents(events) {
  const isOut = {};
  for (const ev of [...events].sort((a, b) => a.t - b.t)) {
    if (ev.type === "out") isOut[ev.pid] = true;
    else if (ev.type === "back") isOut[ev.pid] = false;
  }
  return isOut;
}

const pad = (n) => String(n).padStart(2, "0");

// "HH:MM" in local time, for <input type="time">.
export function toClockInput(t) {
  const d = new Date(t);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// The moment an "HH:MM" means, near `anchor`: the same clock time on the
// anchor's day, the day before or the day after, whichever is closest. So a
// game that runs past midnight edits the way people expect.
export function resolveClockTime(hhmm, anchor) {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm || ""));
  if (!m) return null;
  let best = null;
  for (const dayShift of [-1, 0, 1]) {
    const d = new Date(anchor);
    d.setDate(d.getDate() + dayShift);
    d.setHours(Number(m[1]), Number(m[2]), 0, 0);
    const t = d.getTime();
    if (best === null || Math.abs(t - anchor) < Math.abs(best - anchor)) best = t;
  }
  return best;
}
